import { Alert, Box, Group, Stack, Text, useMantineTheme } from '@mantine/core'
import { GeoJson, GeoJsonFeature } from 'pigeon-maps'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DrivenSegmentRecord,
  GeographicCoordinates,
  SkippedSegmentRecord,
  UnexpectedEventNavigationPatrolVehicle,
} from '@/api/main/types.gen'
import MapViewer from '@/components/MapViewer'
import SVGOverlay from '@/components/SVGOverlay'
import VehiclePositionOverlay from '@/components/VehiclePositionOverlay'

import CopyableId from './CopyableId'
import { EventLocation } from './eventLocation'

type Props = {
  patrolVehicle?: UnexpectedEventNavigationPatrolVehicle
  drivenSegments: Array<DrivenSegmentRecord>
  skippedSegments: Array<SkippedSegmentRecord>
  segmentsGeojson: Record<string, unknown>
  mapPosition?: EventLocation
  zoom?: number
  onZoomChange?: (zoom: number) => void
  height?: number | string
}

type SegmentSelection = { kind: 'driven' | 'skipped'; index: number; streetSegmentId: string } | null

const DriveSegmentsMap = ({
  patrolVehicle,
  drivenSegments,
  skippedSegments,
  segmentsGeojson,
  mapPosition,
  zoom = 16,
  onZoomChange,
  height = 400,
}: Props) => {
  const { t } = useTranslation('unexpectedEvents')
  const theme = useMantineTheme()
  const [selected, setSelected] = useState<SegmentSelection>(null)

  if (!mapPosition) {
    return (
      <Alert color="gray" variant="light">
        {t('header.noGps')}
      </Alert>
    )
  }

  const drivenSegmentStyle = { strokeWidth: 3, stroke: theme.colors.blue[7] }
  const skippedSegmentStyle = { strokeWidth: 5, stroke: theme.colors.orange[7] }
  const selectedSegmentStyle = { strokeWidth: 6, stroke: theme.colors.red[7] }

  const vehiclePosition: GeographicCoordinates | undefined =
    patrolVehicle?.position?.latitude !== undefined &&
    patrolVehicle?.position?.longitude !== undefined &&
    patrolVehicle.position.latitude !== null &&
    patrolVehicle.position.longitude !== null
      ? { latitude: patrolVehicle.position.latitude!, longitude: patrolVehicle.position.longitude! }
      : undefined

  return (
    <Stack gap="xs">
      <MapViewer
        sx={{ height }}
        center={mapPosition}
        zoom={zoom}
        onZoom={onZoomChange}
        overlays={[
          <GeoJson key="drivenSegments">
            {drivenSegments.map((segment, i) => {
              const geometry = segmentsGeojson[segment.street_segment_id]
              if (!geometry) return null
              const isSelected = selected?.kind === 'driven' && selected.index === i
              return (
                <GeoJsonFeature
                  key={`driven-${i}-${segment.street_segment_id}`}
                  feature={{ type: 'Feature', geometry: geometry as GeoJSON.Geometry, properties: {} }}
                  svgAttributes={isSelected ? selectedSegmentStyle : drivenSegmentStyle}
                  onClick={() => setSelected({ kind: 'driven', index: i, streetSegmentId: segment.street_segment_id })}
                />
              )
            })}
          </GeoJson>,
          <GeoJson key="skippedSegments">
            {skippedSegments.map((segment, i) => {
              const geometry = segmentsGeojson[segment.street_segment_id]
              if (!geometry) return null
              const isSelected = selected?.kind === 'skipped' && selected.index === i
              return (
                <GeoJsonFeature
                  key={`skipped-${i}-${segment.street_segment_id}`}
                  feature={{ type: 'Feature', geometry: geometry as GeoJSON.Geometry, properties: {} }}
                  svgAttributes={isSelected ? selectedSegmentStyle : skippedSegmentStyle}
                  onClick={() => setSelected({ kind: 'skipped', index: i, streetSegmentId: segment.street_segment_id })}
                />
              )
            })}
          </GeoJson>,
          <SVGOverlay key="patrolVehicle">
            {scaleFnc =>
              vehiclePosition ? (
                <VehiclePositionOverlay
                  scaleFnc={scaleFnc}
                  position={vehiclePosition}
                  azimuth={patrolVehicle?.azimuth ?? 0}
                />
              ) : null
            }
          </SVGOverlay>,
        ]}
      />
      <Box>
        {selected ? (
          <Group gap="xs" align="center">
            <Text size="sm" fw={500}>
              {t('panels.selectedSegment')}:
            </Text>
            <CopyableId value={selected.streetSegmentId} />
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            {t('panels.selectedSegment')}: —
          </Text>
        )}
      </Box>
    </Stack>
  )
}

export default DriveSegmentsMap
